import { Stack, useFocusEffect } from 'expo-router';
import Head from 'expo-router/head';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { ScrollView } from 'react-native';

import { ScreenContent } from '@fhs/screen-content';
import { Box, Header } from '@fhs-legacy/universal-components';
import Footer from 'components/footer';
import LoadingAnimation from 'components/loading-animation';
import ModalOrderUnavailable from 'components/modal-order-unavailable';
import { RecentItemsCarousel } from 'components/recent-items-carousel';
import { ResponsiveCarouselWrapper } from 'components/responsive-carousel';
import { useRecentItemsAvailable } from 'hooks/use-recent-items-available';
import { useUserOrders } from 'hooks/use-user-orders';
import { useAuthContext } from 'state/auth';
import { useOrderContext } from 'state/order';
import { focusableLabeledA11yProps } from 'utils/accessibility/focusable-labeled-props';
import { useFlag } from 'utils/launchdarkly';
import logger from 'utils/logger';

import AppOrder from './app-order';
import OrdersEmpty from './orders-empty';
import RestaurantOrder from './restaurant-order';
import {
  OfferDisclaimer,
  OfferDisclaimerBold,
  OrdersContainer,
  OrdersEmptyWrapper,
} from './styled.base';
import { useUnavailableItemsOverrideProps } from './use-unavailable-items-override-props';

export const AccountOrders = () => {
  const enableReorder = useFlag('enable-reorder');
  const { unavailableCartEntries, loadingEnrichCartEntries } = useOrderContext();
  const { isAuthenticated } = useAuthContext();
  const { loading, orders, refetch } = useUserOrders({}, 'no-cache');

  useFocusEffect(
    useCallback(() => {
      if (!isAuthenticated) {
        return;
      }

      refetch().catch(error => {
        logger.warn({ error, message: 'Unable to refresh recent orders' });
      });
    }, [isAuthenticated, refetch])
  );

  const { formatMessage } = useIntl();
  const showItems = useRecentItemsAvailable();
  const enableRecentItemsAddToCart = useFlag('enable-recent-items-add-to-cart');
  const [showOrderUnavailableModal, setShowOrderUnavailableModal] = useState(false);
  const handleUnavailableModalDismiss = useCallback(() => {
    setShowOrderUnavailableModal(false);
  }, []);
  const modalOrderUnavailableOverrideProps = useUnavailableItemsOverrideProps();

  useEffect(() => {
    if (unavailableCartEntries.length) {
      setShowOrderUnavailableModal(true);
    }
  }, [unavailableCartEntries.length]);

  const hasOrders = !!orders.length;
  const recentOrdersTitleText = formatMessage({ id: 'recentOrders' });
  const recentOrdersDisclaimerText = formatMessage({ id: 'recentOrdersDisclaimer' });
  const recentOrdersNotAllReorderableText = formatMessage({ id: 'recentOrdersNotAllReorderable' });
  const recentOrdersReorderEligibilityText = formatMessage(
    { id: 'recentOrdersReorderEligibility' },
    { notAllReorderable: recentOrdersNotAllReorderableText }
  );

  if (showOrderUnavailableModal) {
    return (
      <ScreenContent after={<Footer />}>
        <Head>
          <title>{`${recentOrdersTitleText} - Firehouse Subs`}</title>
        </Head>
        <ModalOrderUnavailable
          items={unavailableCartEntries}
          onDismiss={handleUnavailableModalDismiss}
          {...modalOrderUnavailableOverrideProps}
        />
      </ScreenContent>
    );
  }

  return (
    <ScreenContent after={<Footer />} pointerEvents={loadingEnrichCartEntries ? 'none' : 'auto'}>
      <Stack.Screen options={{ title: recentOrdersTitleText }} />
      <Head>
        <title>{`${recentOrdersTitleText} - Firehouse Subs`}</title>
      </Head>
      {showItems && (
        <OrdersContainer>
          {enableRecentItemsAddToCart && (
            <ResponsiveCarouselWrapper header={formatMessage({ id: 'recentItems' })}>
              <RecentItemsCarousel
                locationName="Recent Orders Page"
                containerHasTopPadding={false}
              />
            </ResponsiveCarouselWrapper>
          )}
        </OrdersContainer>
      )}
      {hasOrders && (
        <Box mt="$7" {...focusableLabeledA11yProps(recentOrdersTitleText)}>
          <Header
            accessibilityRole="header"
            accessibilityLevel={2}
            variant="headerOne"
            textAlign="center"
            importantForAccessibility="no-hide-descendants"
          >
            {recentOrdersTitleText}
          </Header>
        </Box>
      )}
      {loading ? (
        <LoadingAnimation mt="$4" />
      ) : (
        <OrdersContainer>
          {hasOrders ? (
            <>
              {enableReorder && (
                <Box
                  {...focusableLabeledA11yProps(
                    `${recentOrdersDisclaimerText} ${recentOrdersReorderEligibilityText}`
                  )}
                >
                  <OfferDisclaimer importantForAccessibility="no-hide-descendants">
                    {recentOrdersDisclaimerText}
                  </OfferDisclaimer>
                  <OfferDisclaimer importantForAccessibility="no-hide-descendants">
                    {formatMessage(
                      { id: 'recentOrdersReorderEligibility' },
                      {
                        notAllReorderable: (
                          <OfferDisclaimerBold>
                            {recentOrdersNotAllReorderableText}
                          </OfferDisclaimerBold>
                        ),
                      }
                    )}
                  </OfferDisclaimer>
                </Box>
              )}
              <ScrollView>
                {orders?.map((order, index) =>
                  // all orders with rbiOrderId === loyaltyTransaction.id are in-restaurant orders
                  order.rbiOrderId === order.loyaltyTransaction?.id ? (
                    <RestaurantOrder key={index} order={order} />
                  ) : (
                    <AppOrder key={index} order={order} />
                  )
                )}
              </ScrollView>
            </>
          ) : (
            <OrdersEmptyWrapper>
              <OrdersEmpty />
            </OrdersEmptyWrapper>
          )}
        </OrdersContainer>
      )}
    </ScreenContent>
  );
};
